import React, { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { PageHeader, ActionBar } from "@/components/PageBits";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Search, Plus, Pencil, Trash2, Eye, EyeOff, Copy, Lock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const NONE = "__none__";
const MASKED = "••••••••";

const empty = {
  email: "", name: "", password: "",
  role: "innendienst", is_active: true, is_subcontractor: false,
  telekom_konto: "", adresse: "", handynummer: "",
  eintrittsdatum: "", austrittsdatum: "", team: "", bemerkung: "",
  auftragsinfo_benutzername: "", auftragsinfo_kennwort: "",
  kasys_benutzername: "", kasys_kennwort: "",
  standardfahrzeug_id: "",
};

function PasswordInput({ value, onChange, dataTestid, readOnly, masked, canSee }) {
  const [show, setShow] = useState(false);
  const isVisible = show && canSee;
  return (
    <div className="relative">
      <Input
        className="h-11 mt-1 pr-10"
        type={isVisible ? "text" : "password"}
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        readOnly={readOnly || masked}
        placeholder={masked ? MASKED : ""}
        data-testid={dataTestid}
      />
      {canSee && !readOnly && (
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          className="absolute right-2 top-1/2 mt-0.5 -translate-y-1/2 p-1 text-slate-400 hover:text-[#0A2540]"
          aria-label={isVisible ? "Verbergen" : "Anzeigen"}
        >
          {isVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      )}
      {(masked || !canSee) && (
        <Lock className="h-4 w-4 absolute right-3 top-1/2 mt-0.5 -translate-y-1/2 text-slate-300" />
      )}
    </div>
  );
}

export default function Employees() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [u, v] = await Promise.all([api.get("/users"), api.get("/vehicles")]);
    setItems(u.data); setVehicles(v.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => items.filter((u) =>
    !q || [u.name, u.email, u.telekom_konto, u.role, u.team, u.handynummer].filter(Boolean).join(" ").toLowerCase().includes(q.toLowerCase())
  ), [items, q]);

  const startNew = () => { setEditingId(null); setForm(empty); setOpen(true); };

  const startEdit = async (u) => {
    setEditingId(u.id);
    let creds = { auftragsinfo_kennwort: u.auftragsinfo_kennwort || "", kasys_kennwort: u.kasys_kennwort || "" };
    if (isAdmin) {
      try {
        const { data } = await api.get(`/users/${u.id}/credentials`);
        creds = { auftragsinfo_kennwort: data.auftragsinfo_kennwort, kasys_kennwort: data.kasys_kennwort };
      } catch {}
    }
    setForm({ ...empty, ...u, ...creds, password: "" });
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name) { toast.error("Name ist erforderlich"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      // Innendienst kann Passwortfelder nicht ändern — strippen
      if (!isAdmin) {
        delete payload.auftragsinfo_kennwort;
        delete payload.kasys_kennwort;
        delete payload.password;
        delete payload.role;
      }
      if (editingId) {
        if (!payload.password) delete payload.password;
        delete payload.email;
        // Don't send placeholder masked values
        if (payload.auftragsinfo_kennwort === MASKED) delete payload.auftragsinfo_kennwort;
        if (payload.kasys_kennwort === MASKED) delete payload.kasys_kennwort;
        await api.put(`/users/${editingId}`, payload);
      } else {
        if (!payload.email) { toast.error("E-Mail ist erforderlich"); setSaving(false); return; }
        if (!payload.password) { toast.error("Passwort ist erforderlich"); setSaving(false); return; }
        await api.post("/auth/register", payload);
      }
      toast.success("Mitarbeiter gespeichert");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const del = async (u) => {
    if (!window.confirm(`Mitarbeiter ${u.email} wirklich löschen?`)) return;
    try { await api.delete(`/users/${u.id}`); load(); toast.success("Gelöscht"); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const toggleActive = async (u) => {
    try { await api.put(`/users/${u.id}`, { is_active: !u.is_active }); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const copyCreds = async (u) => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get(`/users/${u.id}/credentials`);
      const text =
`Mitarbeiter: ${u.name}
E-Mail: ${u.email}

— Auftragsinfo —
Benutzer: ${data.auftragsinfo_benutzername || "—"}
Kennwort: ${data.auftragsinfo_kennwort || "—"}

— KASYS —
Benutzer: ${data.kasys_benutzername || "—"}
Kennwort: ${data.kasys_kennwort || "—"}`;
      await navigator.clipboard.writeText(text);
      toast.success("Zugangsdaten kopiert");
    } catch (e) {
      toast.error("Kopieren fehlgeschlagen");
    }
  };

  return (
    <div data-testid="employees-page">
      <PageHeader
        title="Mitarbeiter"
        subtitle={`${items.length} Mitarbeiter im System`}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="emp-new-btn"
                className="h-12 px-5 bg-[#0A2540] hover:bg-[#113A63] text-white"
                onClick={startNew}
                disabled={!isAdmin}
              >
                <Plus className="h-4 w-4 mr-2" /> Neuer Mitarbeiter
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingId ? "Mitarbeiter bearbeiten" : "Neuer Mitarbeiter"}</DialogTitle>
              </DialogHeader>

              <Tabs defaultValue="personal">
                <TabsList>
                  <TabsTrigger value="personal" data-testid="tab-personal">Persönliche Daten</TabsTrigger>
                  <TabsTrigger value="telekom" data-testid="tab-telekom">Telekom Systeme</TabsTrigger>
                  <TabsTrigger value="fahrzeug" data-testid="tab-fahrzeug">Fahrzeug</TabsTrigger>
                </TabsList>

                <TabsContent value="personal" className="mt-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2"><Label>Mitarbeitername *</Label><Input className="h-11 mt-1" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="emp-name" /></div>
                    <div className="col-span-2"><Label>Adresse</Label><Input className="h-11 mt-1" value={form.adresse} onChange={(e) => set("adresse", e.target.value)} data-testid="emp-adresse" /></div>
                    <div><Label>Handynummer</Label><Input className="h-11 mt-1" value={form.handynummer} onChange={(e) => set("handynummer", e.target.value)} data-testid="emp-handy" /></div>
                    <div><Label>E-Mail *</Label><Input className="h-11 mt-1" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} disabled={!!editingId} data-testid="emp-email" /></div>
                    <div><Label>Eintrittsdatum</Label><Input className="h-11 mt-1" type="date" value={form.eintrittsdatum || ""} onChange={(e) => set("eintrittsdatum", e.target.value)} data-testid="emp-eintritt" /></div>
                    <div><Label>Austrittsdatum</Label><Input className="h-11 mt-1" type="date" value={form.austrittsdatum || ""} onChange={(e) => set("austrittsdatum", e.target.value)} data-testid="emp-austritt" /></div>
                    <div>
                      <Label>Rolle</Label>
                      <Select value={form.role} onValueChange={(v) => set("role", v)} disabled={!isAdmin}>
                        <SelectTrigger className="h-11 mt-1" data-testid="emp-role"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="innendienst">Innendienst</SelectItem>
                          <SelectItem value="buchhaltung">Buchhaltung</SelectItem>
                          <SelectItem value="disponent">Disponent</SelectItem>
                          <SelectItem value="techniker">Techniker</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Team</Label><Input className="h-11 mt-1" value={form.team || ""} onChange={(e) => set("team", e.target.value)} data-testid="emp-team" /></div>
                    <div className="flex items-center justify-between bg-slate-50 p-3 rounded">
                      <Label>Aktiv</Label>
                      <Switch checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} data-testid="emp-active" />
                    </div>
                    <div className="flex items-center justify-between bg-slate-50 p-3 rounded">
                      <Label>Subunternehmer</Label>
                      <Switch checked={form.is_subcontractor} onCheckedChange={(v) => set("is_subcontractor", v)} data-testid="emp-subcontractor" />
                    </div>
                    <div className="col-span-2"><Label>Telekom Konto</Label><Input className="h-11 mt-1" value={form.telekom_konto || ""} onChange={(e) => set("telekom_konto", e.target.value)} data-testid="emp-telekom" /></div>
                    <div className="col-span-2"><Label>Bemerkung</Label><Textarea className="mt-1 min-h-[80px]" value={form.bemerkung || ""} onChange={(e) => set("bemerkung", e.target.value)} data-testid="emp-bemerkung" /></div>
                    {isAdmin && (
                      <div className="col-span-2">
                        <Label>{editingId ? "Login-Passwort (leer = unverändert)" : "Login-Passwort *"}</Label>
                        <PasswordInput value={form.password} onChange={(v) => set("password", v)} dataTestid="emp-password" canSee={true} />
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="telekom" className="mt-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2 text-xs uppercase tracking-wider font-semibold text-slate-500">Auftragsinfo</div>
                    <div><Label>Benutzername</Label><Input className="h-11 mt-1" value={form.auftragsinfo_benutzername || ""} onChange={(e) => set("auftragsinfo_benutzername", e.target.value)} data-testid="emp-auf-user" /></div>
                    <div>
                      <Label>Kennwort</Label>
                      <PasswordInput value={form.auftragsinfo_kennwort} onChange={(v) => set("auftragsinfo_kennwort", v)} dataTestid="emp-auf-pw" canSee={isAdmin} masked={!isAdmin} />
                    </div>
                    <div className="col-span-2 text-xs uppercase tracking-wider font-semibold text-slate-500 mt-2">KASYS</div>
                    <div><Label>Benutzername</Label><Input className="h-11 mt-1" value={form.kasys_benutzername || ""} onChange={(e) => set("kasys_benutzername", e.target.value)} data-testid="emp-kasys-user" /></div>
                    <div>
                      <Label>Kennwort</Label>
                      <PasswordInput value={form.kasys_kennwort} onChange={(v) => set("kasys_kennwort", v)} dataTestid="emp-kasys-pw" canSee={isAdmin} masked={!isAdmin} />
                    </div>
                    {!isAdmin && <div className="col-span-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">Kennwörter können nur von Administratoren angesehen oder geändert werden.</div>}
                  </div>
                </TabsContent>

                <TabsContent value="fahrzeug" className="mt-4">
                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <Label>Standardfahrzeug</Label>
                      <Select value={form.standardfahrzeug_id || NONE} onValueChange={(v) => set("standardfahrzeug_id", v === NONE ? "" : v)}>
                        <SelectTrigger className="h-11 mt-1" data-testid="emp-fahrzeug"><SelectValue placeholder="— wählen —" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NONE}>— keins —</SelectItem>
                          {vehicles.filter((v) => v.is_active).map((v) => (
                            <SelectItem key={v.id} value={v.id}>{v.kennzeichen} — {v.fahrzeug}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              <DialogFooter>
                <Button
                  className="h-11 bg-[#0A2540] hover:bg-[#113A63] text-white"
                  onClick={save}
                  disabled={saving}
                  data-testid="emp-save"
                >
                  {saving ? "Speichern…" : "Speichern"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="relative max-w-md mb-4">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input className="h-11 pl-10" placeholder="Name, E-Mail, Team, Handy…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="emp-search" />
      </div>
      <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "#E2E8F0" }}>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">E-Mail</th>
              <th className="text-left px-4 py-3">Team</th>
              <th className="text-left px-4 py-3">Handy</th>
              <th className="text-left px-4 py-3">Rolle</th>
              <th className="text-left px-4 py-3">Subunternehmer</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="w-56"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-slate-400">Keine Mitarbeiter</td></tr>}
            {filtered.map((u) => (
              <tr key={u.id} className="border-t" style={{ borderColor: "#E2E8F0" }}>
                <td className="px-4 py-2.5 font-semibold">{u.name}</td>
                <td className="px-4 py-2.5">{u.email}</td>
                <td className="px-4 py-2.5">{u.team}</td>
                <td className="px-4 py-2.5 mono text-xs">{u.handynummer}</td>
                <td className="px-4 py-2.5"><span className="text-xs font-semibold uppercase tracking-wider text-slate-600">{u.role}</span></td>
                <td className="px-4 py-2.5">{u.is_subcontractor ? "Ja" : "Nein"}</td>
                <td className="px-4 py-2.5"><span className={`text-xs font-semibold ${u.is_active ? "text-emerald-700" : "text-slate-400"}`}>{u.is_active ? "Aktiv" : "Inaktiv"}</span></td>
                <td className="px-4 py-2.5 flex justify-end gap-1 flex-wrap">
                  <button className="p-1.5 hover:text-[#0A2540]" onClick={() => startEdit(u)} data-testid={`emp-edit-${u.email}`} title="Bearbeiten"><Pencil className="h-4 w-4" /></button>
                  {isAdmin && (
                    <button className="p-1.5 hover:text-[#0A2540]" onClick={() => copyCreds(u)} data-testid={`emp-copy-${u.email}`} title="Zugangsdaten kopieren"><Copy className="h-4 w-4" /></button>
                  )}
                  {isAdmin && (
                    <Button variant="outline" size="sm" onClick={() => toggleActive(u)} data-testid={`emp-toggle-${u.email}`}>{u.is_active ? "Deaktivieren" : "Aktivieren"}</Button>
                  )}
                  {isAdmin && (
                    <button className="p-1.5 hover:text-red-600" onClick={() => del(u)} data-testid={`emp-del-${u.email}`} title="Löschen"><Trash2 className="h-4 w-4" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ActionBar onPrint={() => window.print()} hideNext />
    </div>
  );
}
